import * as Sentry from '@sentry/react'
import pkjson from '../../package.json'

type Exception = {
  stack: string
  message: string
}

const initSentry = () => {
  const getMicroInstances = () => {
    const wsm = window.__SENTRY_MICRO__

    // eslint-disable-next-line no-undefined
    if (wsm === undefined || wsm.instances === undefined) {
      return {}
    }

    return wsm.instances
  }

  Sentry.init({
    dsn: 'https://0938ad685b8e4fb2a2bf8d38b08b0bb1@o49094.ingest.sentry.io/6075468',
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
    environment: process.env.NODE_ENV === 'production' ? 'production' : 'staging',
    release: `app-${pkjson.name}@${pkjson.version}`,
    tracesSampleRate: 1.0,
    attachStacktrace: true,
    maxValueLength: 5000,
    beforeSend: (event, hint) => {
      const micros = getMicroInstances()
      const stack = (hint.originalException as Exception)?.stack || hint.syntheticException?.stack

      // eslint-disable-next-line guard-for-in
      for (const iname in micros) {
        const micro = micros[iname]

        if (stack?.match(micro.matcher)) {
          event.release = micro.client.getOptions().release
          micro.client.captureEvent(event)

          return null
        }
      }

      return event
    },
    ignoreErrors: [
      'Network Error',
      'Error: Network Error',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      'Request failed with status code 500',
      'AbortError: The transaction was aborted, so the request cannot be fulfilled.',

      // Random plugins/extensions
      'top.GLOBALS',
      /* eslint-disable quotes */
      "Unexpected token '<'",
      'Extension context invalidated.',
      "SecurityError: Failed to read the 'cssRules' property from 'CSSStyleSheet': Cannot access rules",
      "Cannot read property 'isMew' of undefined",
      "Cannot read property 'removeChild' of null",
      'Load timeout for modules: main http://requirejs.org/docs/errors.html#timeout',
      "Cannot set property 'className' of null",
      "Cannot read property 'toolBar' of undefined",
      "Cannot read property 'query' of undefined",
      "Cannot read property 'NaN' of undefined",
      "Identifier 'originalPrompt' has already been declared",
      'There is no clipping info for given tab',
      'x5onSkinSwitch is not defined',
      'KasperskyLab is not defined',
      'ibPauseAllVideos is not defined',
      'ibFindAllVideos is not defined',
      "undefined is not an object (evaluating 'safari.self.tab.setContextMenuEventUserInfo')",

      // iOS error related to google app search
      "null is not an object (evaluating 'elt.parentNode')",

      // Meteor ERRORS
      'INVALID_STATE_ERR',
      'NS_ERROR_SOCKET_CREATE_FAILED',

      // ignoring requests without object
      'Non-Error promise rejection captured',

      // ignoring gtm.js errors
      'No error message',
      'elements.forEach is not a function',
      "Cannot read property 'innerText' of null",
      "Object doesn't support property or method 'forEach'",
      "Unable to get property 'innerText' of undefined or null reference",
      "null is not an object (evaluating 'element.innerText')",
      "Identifier 'selectors' has already been declared",
      'fbq is not defined',
      "Can't find variable: fbq",

      // avast extension error
      "Cannot read property '_avast_submit' of undefined",

      // Firebase connection errors - omnichat
      'UnknownError: An internal error was encountered in the Indexed Database server',
      'UnknownError: Attempt to get a record from database without an in-progress transaction',
      'PERMISSION_DENIED: Permission denied',
      'Uncaught Error in onSnapshot: FirebaseError: Missing or insufficient permissions.',
      "The project's quota for this operation has been exceeded.",
      'INTERNAL ASSERTION FAILED: Unexpected state',
      'UnknownError: Database deleted by request of the user',

      // Request aborted warnings in console from @hotmart/request
      'Request aborted',

      // External errors
      "Cannot read property 'campaign' of undefined",

      // Navigator share api canceled reject error
      'AbortError: Share canceled',
      'AbortError: Internal error: could not connect to Web Share interface.',
      'AbortError: Abort due to cancellation of share.',

      "null is not an object (evaluating 'a[w.checkoutSettingKeys.devToolsEnabled]')"
      /* eslint-enable quotes */
    ],
    denyUrls: [
      // Google
      /google.com/i,
      /gtm.js/i,

      // Facebook
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,

      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,

      // Firefox extensions
      /^resource:\/\//i,
      /^moz-extension:\/\//i
    ]
  })
}

if (process.env.NODE_ENV !== 'development') {
  initSentry()
}
